import React, { useState, useEffect, useRef } from "react";
import { isValid } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { apiClient, getRequest } from "../../api/client";
import { useOverviewChartsEvents } from "../../hooks/useOverviewChartsEvents";
import { stagingGetRequest } from "../../api/authClient";
import { useStoreState } from "../../store/easy-peasy/hooks";
import {
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  BarChart3,
  TrendingUp,
  Zap,
  Camera,
  ShieldAlert,
  Clock3,
  Radar,
  UserX,
  AlertTriangle,
  Siren,
  RefreshCw,
} from "lucide-react";
import chartsConfig from "../../Components/Dashboard/Overview/Charts/Chart_Config/ChartConfig.json";
import { OverviewElasticChart } from "../../Components/Dashboard/Overview/OverviewElasticChart";

const cfg = chartsConfig as { group_filter?: { events?: { select_status?: string[]; select_slots?: string[] }; analytics?: { select_slots?: string[]; select_classes?: string[] } } };
const statusOptions = cfg?.group_filter?.events?.select_status ?? ["Resolved", "Unresolved", "Rejected"];
const eventSlotsOptions = cfg?.group_filter?.events?.select_slots ?? ["Morning", "Evening", "Night"];
const analyticsSlotsOptions = cfg?.group_filter?.analytics?.select_slots ?? ["Morning", "Evening", "Night"];

type MetricCard = {
  id: string;
  label: string;
  value: string | number;
  icon: React.ReactNode;
  badge?: string;
  accent: "magenta" | "blue" | "green" | "red" | "orange" | "yellow";
};

const METRIC_ICON_POOL = [
  <ShieldAlert className="w-5 h-5" />,
  <Clock3 className="w-5 h-5" />,
  <Radar className="w-5 h-5" />,
  <UserX className="w-5 h-5" />,
  <AlertTriangle className="w-5 h-5" />,
  <Siren className="w-5 h-5" />,
  <TrendingUp className="w-5 h-5" />,
];

const getMetricIcon = (label: string, index: number) => {
  const normalized = label.toLowerCase();
  if (normalized.includes("unauthorised") || normalized.includes("unauthorized")) {
    return <ShieldAlert className="w-5 h-5" />;
  }
  if (normalized.includes("unattended")) {
    return <Clock3 className="w-5 h-5" />;
  }
  if (normalized.includes("loiter")) {
    return <Radar className="w-5 h-5" />;
  }
  return METRIC_ICON_POOL[index % METRIC_ICON_POOL.length];
};

const QUICK_PRESETS = [
  { label: "1 Week", days: 7 },
  { label: "1 Month", days: 30 },
  { label: "3 Months", days: 90 },
  { label: "6 Months", days: 180 },
  { label: "1 Year", days: 365 },
  // { label: "2 Years", days: 730 },
];

function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultDateFrom(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return toDateString(d);
}

function defaultDateTo(): string {
  return toDateString(new Date());
}

/** Parse YYYY-MM-DD as a local calendar day (avoids UTC midnight shifts). */
function parseLocalDateYmd(ymd: string): Date | null {
  if (!ymd) return null;
  const parts = ymd.split("-").map((v) => Number(v));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  return isValid(date) ? date : null;
}

function ymdToIso(ymd: string, endOfDay: boolean): string {
  const d = parseLocalDateYmd(ymd);
  if (!d) return new Date().toISOString();
  d.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  return d.toISOString();
}

/** For `vgi-events` and `vgi-analytics` dashboards: pie → slot → all other chart types (stable tie-break by title). */
function sortElasticQueriesPieSlotFirst<T extends { type?: string; title?: string }>(rows: T[]): T[] {
  const tier = (raw: string | undefined): number => {
    const t = (raw ?? "").trim().toLowerCase();
    if (t === "pie") return 0;
    if (t === "slot") return 1;
    return 2;
  };
  return [...rows].sort((a, b) => {
    const d = tier(a.type) - tier(b.type);
    if (d !== 0) return d;
    return String(a.title ?? "").localeCompare(String(b.title ?? ""), undefined, { sensitivity: "base" });
  });
}

export default function Overview() {
  const DROPDOWN_PAGE_SIZE = 10000;
  const [fromDate, setFromDate] = useState(defaultDateFrom);
  const [toDate, setToDate] = useState(defaultDateTo);
  const [appliedFromDate, setAppliedFromDate] = useState(defaultDateFrom);
  const [appliedToDate, setAppliedToDate] = useState(defaultDateTo);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [locationCount, setLocationCount] = useState(0);
  const [applied, setApplied] = useState(false);
  const [appliedLocationIds, setAppliedLocationIds] = useState<string[]>([]);
  const [appliedCameraIds, setAppliedCameraIds] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [selectedAnalyticsSlots, setSelectedAnalyticsSlots] = useState<string[]>([]);
  // Draft (selected*) updates as user clicks checkboxes; applied* updates only when Apply is clicked.
  const [appliedEventsFilters, setAppliedEventsFilters] = useState<{
    Events?: string[];
    Status?: string[];
    Slots?: string[];
  }>({});
  const [appliedAnalyticsFilters, setAppliedAnalyticsFilters] = useState<{
    Slots?: string[];
    Classes?: string[];
  }>({});
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [classesDropdownOpen, setClassesDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [slotsDropdownOpen, setSlotsDropdownOpen] = useState(false);
  const [analyticsSlotsDropdownOpen, setAnalyticsSlotsDropdownOpen] = useState(false);
  /** Bumps only when Events Analytics Apply runs — events-index elastic charts refetch. */
  const [eventsElasticApplyTrigger, setEventsElasticApplyTrigger] = useState(0);
  /** Bumps only when Analytic Graphs Apply runs — analytics-index elastic charts refetch. */
  const [analyticsElasticApplyTrigger, setAnalyticsElasticApplyTrigger] = useState(0);
  const [globalApplyTrigger, setGlobalApplyTrigger] = useState(0);
  const metricsScrollRef = useRef<HTMLDivElement>(null); // scrollable cards only
  const [locationsOpen, setLocationsOpen] = useState(false);
  const locationsRef = useRef<HTMLDivElement>(null);
  const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
  const [cameraCount, setCameraCount] = useState(0);
  const [autoSelectAllLocations, setAutoSelectAllLocations] = useState(true);
  const [autoSelectAllCameras, setAutoSelectAllCameras] = useState(true);
  const [locationSearchText, setLocationSearchText] = useState("");
  const [cameraSearchText, setCameraSearchText] = useState("");
  const [camerasOpen, setCamerasOpen] = useState(false);
  const camerasRef = useRef<HTMLDivElement>(null);
  const [selectionToast, setSelectionToast] = useState<{ message: string; source: "location" | "camera" } | null>(null);
  const selectionToastTimerRef = useRef<number | null>(null);
  // Hide scrollbar for this page only (the scroll container lives in MainLayout).
  useEffect(() => {
    document.body.classList.add("overview-hide-scrollbar");
    return () => {
      document.body.classList.remove("overview-hide-scrollbar");
    };
  }, []);

  // Use store (MainLayout sets via setUserFromMe after auth/me) so location search runs on page load
  const storeUserId = useStoreState((state) => state.user?.id ?? "");
  const [initClientId] = useState<string>(() => {
    const fromStorage =
      localStorage.getItem("auth_user_id") ?? localStorage.getItem("clientId");
    if (fromStorage) return fromStorage;
    try {
      const authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
      return authUser?.id ?? "";
    } catch {
      return "";
    }
  });
  const clientId = storeUserId || initClientId;
  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("auth_user") ?? "{}");
    } catch {
      return {};
    }
  })();
  const authRoles: string[] = Array.isArray(authUser?.roles) ? authUser.roles : [];
  const isViewer = authRoles.some((r) => String(r).toUpperCase() === "VIEWER");
  const viewerCreatorId = String(authUser?.creator_id ?? authUser?.creatorId ?? "").trim();
  const effectiveClientId = isViewer && viewerCreatorId ? viewerCreatorId : clientId;
  const withClientDetails = {
    role: Array.isArray(authUser?.roles)
      ? authUser.roles
      : authUser?.role
      ? [authUser.role]
      : [],
    creatorId:
      authUser?.creator_id ??
      authUser?.creatorId ??
      (isViewer ? effectiveClientId : ""),
    id: authUser?.id ?? clientId ?? "",
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const viewerLocationDetails = Array.isArray(authUser?.viewer_location_details)
    ? authUser.viewer_location_details
    : [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const viewerCameraDetails = Array.isArray(authUser?.viewer_camera_details)
    ? authUser.viewer_camera_details
    : [];

  const showSelectionToast = (message: string, source: "location" | "camera") => {
    setSelectionToast({ message, source });
    if (selectionToastTimerRef.current) {
      window.clearTimeout(selectionToastTimerRef.current);
    }
    selectionToastTimerRef.current = window.setTimeout(() => {
      setSelectionToast(null);
      selectionToastTimerRef.current = null;
    }, 1800);
  };

  const { data: locationsRes, isLoading: locationsLoading } = useQuery({
    queryKey: ["locations-full", clientId],
    queryFn: () =>
      stagingGetRequest("/location/search", {
        q: "",
        clientId,
        page: 1,
        pageSize: DROPDOWN_PAGE_SIZE,
      }),
    enabled: !!clientId,
  });
  const resolvedLocations = (() => {
    const raw =
      locationsRes?.data?.items ??
      locationsRes?.data ??
      locationsRes?.items ??
      locationsRes?.results ??
      locationsRes?.response?.locations ??
      locationsRes?.locations ??
      [];
    const list = Array.isArray(raw) ? raw : [];
    const mapped = list.map((l: any) => ({
      id: l.id ?? l._id,
      value: l.id ?? l._id ?? l.value,
      label: l.name ?? l.label ?? l.area ?? l.building ?? l.id ?? l._id ?? "—",
    }));
    return Array.from(new Map(mapped.map((loc: any) => [loc.id ?? loc.value, loc])).values());
  })();
  const locationTotal = Number(
    locationsRes?.data?.pagination?.total ??
      locationsRes?.pagination?.total ??
      resolvedLocations.length,
  );

  const allLocationIds = resolvedLocations.map((l: any) => l.id || l.value);
  const filteredLocations = resolvedLocations.filter((loc: any) =>
    String(loc.label ?? loc.name ?? loc.id ?? loc.value ?? "")
      .toLowerCase()
      .includes(locationSearchText.trim().toLowerCase()),
  );
  const isAllSelected = autoSelectAllLocations && selectedLocations.length > 0;
  const loadedLocationCount = resolvedLocations.length;
  const allLocationsLoaded = locationCount > 0 && loadedLocationCount >= locationCount;
  const locationSelectionDisplayCount =
    isAllSelected && !locationsLoading
      ? `${loadedLocationCount.toLocaleString()}/${locationCount.toLocaleString()}`
      : `${selectedLocations.length.toLocaleString()}`;
  const hasLocationsSelected = selectedLocations.length > 0;
  const { data: camerasRes, isLoading: camerasLoading } = useQuery({
    queryKey: ["cameras-full", clientId, selectedLocations, isAllSelected, allLocationIds],
    queryFn: () => {
      const locationIds = isAllSelected ? allLocationIds : selectedLocations;
      return stagingGetRequest("/camera/search", {
        clientId,
        page: 1,
        pageSize: DROPDOWN_PAGE_SIZE,
        locationIds,
      });
    },
    enabled: !!clientId && hasLocationsSelected,
  });
  const resolvedCameras = (() => {
    const raw =
      camerasRes?.data?.items ??
      camerasRes?.data ??
      camerasRes?.items ??
      camerasRes?.results ??
      camerasRes?.response?.cameras ??
      camerasRes?.cameras ??
      [];
    const list = Array.isArray(raw) ? raw : [];
    const mapped = list.map((c: any) => ({
      id: c.id ?? c._id,
      value: c.id ?? c._id ?? c.value,
      label: c.aliasName ?? c.name ?? c.label ?? c.title ?? c.id ?? c._id ?? "—",
      locationId: c.location?.id ?? c.locationId ?? c.location_id,
    }));
    const deduped = Array.from(new Map(mapped.map((cam: any) => [cam.id ?? cam.value, cam])).values());
    // Show only cameras that belong to selected locations
    const locIds = isAllSelected ? allLocationIds : selectedLocations;
    if (locIds.length > 0) {
      return deduped.filter((c: any) => c.locationId && locIds.includes(c.locationId));
    }
    return deduped;
  })();
  const cameraTotal = Number(
    camerasRes?.data?.pagination?.total ??
      camerasRes?.pagination?.total ??
      resolvedCameras.length,
  );
  const allCameraIds = resolvedCameras.map((c: any) => c.id ?? c.value);
  const filteredCameras = resolvedCameras.filter((cam: any) =>
    String(cam.label ?? cam.name ?? cam.id ?? cam.value ?? "")
      .toLowerCase()
      .includes(cameraSearchText.trim().toLowerCase()),
  );
  const selectedCamerasInScope = selectedCameras.filter((id) => allCameraIds.includes(id));
  const isAllCamerasSelected =
    autoSelectAllCameras &&
    allCameraIds.length > 0 &&
    allCameraIds.every((id) => selectedCamerasInScope.includes(id));
  const loadedCameraCount = resolvedCameras.length;
  const allCamerasLoaded = cameraCount > 0 && loadedCameraCount >= cameraCount;
  const selectedCameraCount = selectedCamerasInScope.length;
  const cameraSelectionDisplayCount =
    isAllCamerasSelected && !camerasLoading
      ? `${loadedCameraCount.toLocaleString()}/${cameraCount.toLocaleString()}`
      : `${selectedCameraCount.toLocaleString()}`;

  const draftLocationIds = isAllSelected ? allLocationIds : selectedLocations;

  const hasValidDateRange =
    !!parseLocalDateYmd(fromDate) && !!parseLocalDateYmd(toDate);
  const canApplyFilters =
    hasLocationsSelected && selectedCameraCount > 0 && hasValidDateRange;

  const draftCameraIdsForPayload =
    selectedCameraCount > 0
      ? selectedCamerasInScope
      : allCameraIds.length > 0
      ? allCameraIds
      : [];
  const appliedLocationIdsForData = appliedLocationIds.length > 0 ? appliedLocationIds : [];
  const appliedCameraIdsForData = appliedCameraIds.length > 0 ? appliedCameraIds : [];
  const canSyncAppliedFilters = appliedLocationIdsForData.length > 0 && appliedCameraIdsForData.length > 0;

  const baseParams = {
    from: ymdToIso(appliedFromDate, false),
    to: ymdToIso(appliedToDate, true),
    location_id: appliedLocationIdsForData,
    // Pass selected camera IDs to /overview/charts so charts can filter by cameras
    cameraIds: appliedCameraIdsForData,
    clientId,
    withClientDetails,
  };

  /** Smart Filters bump both; each section's Apply bumps only its half (see OverviewElasticChart applyGeneration). */
  const eventsElasticGeneration = globalApplyTrigger + eventsElasticApplyTrigger;
  const analyticsElasticGeneration = globalApplyTrigger + analyticsElasticApplyTrigger;

  const { data: elasticQueriesRes } = useQuery({
    queryKey: ["elastic-queries-overview-list", effectiveClientId],
    queryFn: async () => {
      const res = await apiClient.get("/surveillance/elastic-queries", {
        params: { clientId: effectiveClientId, activeOnly: true, page: 1, pageSize: 500 },
      });
      return res.data as { queries?: Array<{ id: string; title: string; type: string; index: string }> };
    },
    enabled: !!effectiveClientId,
  });

  const elasticRowsAll = (() => {
    const raw = elasticQueriesRes?.queries;
    return Array.isArray(raw) ? raw : [];
  })();

  const isAnalyticsIndex = (q: { index?: string }) =>
    String(q.index ?? "").toLowerCase().includes("analytics");

  const eventsElasticRows = sortElasticQueriesPieSlotFirst(
    elasticRowsAll.filter((q) => !isAnalyticsIndex(q)),
  );
  const analyticsElasticRows = sortElasticQueriesPieSlotFirst(elasticRowsAll.filter(isAnalyticsIndex));

  const { isFetching: elasticSyncFetching, isSuccess: elasticFiltersSynced } = useQuery({
    queryKey: [
      "elastic-queries-dashboard-sync",
      effectiveClientId,
      globalApplyTrigger,
      baseParams.from,
      baseParams.to,
      JSON.stringify(appliedLocationIdsForData),
      JSON.stringify(appliedCameraIdsForData),
    ],
    queryFn: async () => {
      await apiClient.get("/surveillance/elastic-queries/data", {
        params: {
          clientId: effectiveClientId,
          from: baseParams.from,
          to: baseParams.to,
          locationIds: JSON.stringify(appliedLocationIdsForData),
          cameraIds: JSON.stringify(appliedCameraIdsForData),
          syncOnly: true,
        },
      });
      return true;
    },
    enabled: !!clientId && globalApplyTrigger > 0 && canSyncAppliedFilters,
  });

  const elasticChartsFiltersReady = elasticFiltersSynced && !elasticSyncFetching;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const eventsParams = {
    from: ymdToIso(appliedFromDate, false),
    to: ymdToIso(appliedToDate, true),
    location_id: appliedLocationIds,
    cameraIds: appliedCameraIds,
    clientId: effectiveClientId,
    withClientDetails,
    Events: appliedEventsFilters.Events,
    Status: appliedEventsFilters.Status,
    Slots: appliedEventsFilters.Slots,
  };
  const metricsEventsParams = {
    from: ymdToIso(appliedFromDate, false),
    to: ymdToIso(appliedToDate, true),
    location_id: appliedLocationIdsForData,
    cameraIds: appliedCameraIdsForData,
    clientId: effectiveClientId,
    withClientDetails,
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const analyticsParams = {
    ...baseParams,
    Classes: selectedClasses.length > 0 ? selectedClasses : undefined,
    Slots: selectedAnalyticsSlots.length > 0 ? selectedAnalyticsSlots : undefined,
  };

  /** POST `/surveillance/overview/charts/events` — JSON body matches curl (`from`, `to`, `location_id`, `cameraIds`, `clientId`, `withClientDetails`, optional Events/Status/Slots). */
  const { data: eventsRes } = useOverviewChartsEvents(
    metricsEventsParams,
    Boolean(effectiveClientId && appliedLocationIdsForData.length > 0 && appliedCameraIdsForData.length > 0),
  );
  const cleanedEventsData = (() => {
    const res = eventsRes as any;
    const body = res?.payload ?? res?.data ?? res;
    const nestedArray =
      (Array.isArray(body?.data) && body.data) ||
      (Array.isArray(body?.data?.data) && body.data.data) ||
      null;
    if (nestedArray) {
      return nestedArray.map((item: any) => ({
        value: item?.value ?? item?.subcategory ?? item?.label ?? "Unknown",
        label: item?.label ?? item?.subcategory ?? item?.value ?? "Unknown",
        count: Number(item?.count ?? item?.doc_count ?? 0),
      }));
    }
    if (Array.isArray(body)) {
      return body.map((item: any) => ({
        value: item?.value ?? item?.subcategory ?? item?.label ?? "Unknown",
        label: item?.label ?? item?.subcategory ?? item?.value ?? "Unknown",
        count: Number(item?.count ?? item?.doc_count ?? 0),
      }));
    }
    if (Array.isArray(body?.events)) {
      return body.events.map((item: any) => ({
        value: item.value,
        label: item.label || item.value,
        count: item.count ?? 0,
      }));
    }
    // Try ES aggregation buckets (multiple possible paths)
    const bucketPaths = [
      // current backend shape (wrapped in payload)
      body?.aggregations?.all_subcategories?.buckets,
      body?.aggregations?.annotations_nested?.all_subcategories?.buckets,
      body?.aggregations?.annotations_nested?.classes?.buckets,
      body?.aggregations?.annotations_nested?.category_subcategory?.buckets,
    ];
    for (const buckets of bucketPaths) {
      if (Array.isArray(buckets) && buckets.length > 0) {
        return buckets.map((b: any) => ({
          value: b.key,
          label: b.key,
          count: b.doc_count ?? b.docCount ?? 0,
        }));
      }
    }
    // Fallback: use hits.total when we have no buckets (at least show total count)
    const hitsTotal = body?.hits?.total?.value ?? body?.hits?.total;
    if (typeof hitsTotal === "number" && hitsTotal >= 0) {
      return [{ value: "Total", label: "Total", count: hitsTotal }];
    }
    // Legacy response shape
    const legacy =
      body?.response?.events_data?.data?.map((item: any) => ({
        ...item,
        label: (item.label || item.value || "").replace(/^[^-]*-\s*/, ""),
      })) || [];
    return legacy;
  })();

  const { data: classesRes } = useQuery({
    queryKey: ["classes"],
    queryFn: () => getRequest("/surveillance/overview/charts/classes"),
  });
  const cleanedClassesData =
    (classesRes as any)?.classes?.map((item: any) => ({
      ...item,
      label: (item.label || item.value || "").replace(/^[^-]*-\s*/, ""),
    })) || [];

  const locationsInitialized = useRef(false);
  useEffect(() => {
    if (resolvedLocations?.length > 0 && !locationsInitialized.current) {
      const ids = resolvedLocations.map((l: any) => l.id || l.value);
      setLocationCount(locationTotal);
      setSelectedLocations(ids);
      locationsInitialized.current = true;
    } else if (resolvedLocations?.length > 0 && autoSelectAllLocations) {
      const ids = resolvedLocations.map((l: any) => l.id || l.value);
      setSelectedLocations((prev) => {
        const merged = Array.from(new Set([...prev, ...ids]));
        if (merged.length === prev.length && merged.every((id, idx) => id === prev[idx])) {
          return prev;
        }
        return merged;
      });
    }
  }, [resolvedLocations, autoSelectAllLocations, locationTotal]);

  useEffect(() => {
    setLocationCount(locationTotal);
  }, [locationTotal]);

  // Keep camera selection aligned with currently selected locations (drop stale IDs).
  useEffect(() => {
    if (allCameraIds.length === 0) return;

    setSelectedCameras((prev) => {
      if (autoSelectAllCameras) {
        if (
          prev.length === allCameraIds.length &&
          allCameraIds.every((id) => prev.includes(id))
        ) {
          return prev;
        }
        return allCameraIds;
      }

      const pruned = prev.filter((id) => allCameraIds.includes(id));
      if (pruned.length === prev.length) return prev;
      return pruned;
    });
  }, [allCameraIds, autoSelectAllCameras]);

  useEffect(() => {
    setCameraCount(cameraTotal);
  }, [cameraTotal]);

  // Initial one-time apply after dropdown selections are fully initialized.
  useEffect(() => {
    if (applied) return;
    if (selectedLocations.length === 0) return;
    if (selectedCameraCount === 0 && allCameraIds.length === 0) return;

    setAppliedLocationIds(isAllSelected ? allLocationIds : selectedLocations);
    setAppliedCameraIds(
      selectedCameraCount > 0
        ? selectedCamerasInScope
        : allCameraIds.length > 0
        ? allCameraIds
        : [],
    );
    setApplied(true);
    setGlobalApplyTrigger((prev) => prev + 1);
  }, [
    applied,
    selectedLocations,
    selectedCameras,
    selectedCamerasInScope,
    selectedCameraCount,
    isAllSelected,
    allLocationIds,
    allCameraIds,
  ]);

  const eventsDropdownRef = useRef<HTMLDivElement>(null);
  const classesDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const slotsDropdownRef = useRef<HTMLDivElement>(null);
  const analyticsSlotsDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasLocationsSelected) {
      setSelectedCameras([]);
      setAutoSelectAllCameras(true);
    }
  }, [hasLocationsSelected]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (locationsRef.current && !locationsRef.current.contains(target)) setLocationsOpen(false);
      if (camerasRef.current && !camerasRef.current.contains(target)) setCamerasOpen(false);
      if (eventsDropdownRef.current && !eventsDropdownRef.current.contains(target)) setEventsDropdownOpen(false);
      if (classesDropdownRef.current && !classesDropdownRef.current.contains(target)) setClassesDropdownOpen(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) setStatusDropdownOpen(false);
      if (slotsDropdownRef.current && !slotsDropdownRef.current.contains(target)) setSlotsDropdownOpen(false);
      if (analyticsSlotsDropdownRef.current && !analyticsSlotsDropdownRef.current.contains(target)) setAnalyticsSlotsDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (selectionToastTimerRef.current) {
        window.clearTimeout(selectionToastTimerRef.current);
      }
    };
  }, []);

  // Build counters from events data (POST /overview/charts/events when query key changes)
  const counters: any[] = (() => {
    if (Array.isArray(cleanedEventsData) && cleanedEventsData.length > 0) {
      return cleanedEventsData.map((e: any) => ({
        name: e.label || e.value,
        counts: e.count ?? e.doc_count ?? 0,
      }));
    }
    return [];
  })();

  const eventsBody = (() => {
    const res = eventsRes as any;
    return res?.payload ?? res?.data ?? res;
  })();
  const totalCount =
    eventsBody?.totalEvents ??
    counters.reduce((sum: number, c: any) => sum + (c.counts ?? c.count ?? c.doc_count ?? 0), 0);
  const metricCards: MetricCard[] = [
    {
      id: "total",
      label: "Total Events",
      value: totalCount.toLocaleString() + " events",
      icon: <BarChart3 className="w-6 h-6" />,
      badge: "Total",
      accent: "magenta",
    },
  ];
  const countersSorted = [...counters].sort(
    (a: any, b: any) =>
      Number(b?.counts ?? b?.count ?? b?.doc_count ?? 0) - Number(a?.counts ?? a?.count ?? a?.doc_count ?? 0),
  );
  countersSorted.forEach((c: any, i: number) => {
    const label = c.name ?? c.label ?? c.key ?? `Event ${i + 1}`;
    // API often returns a single "Total" bucket (hits fallback) — same as fixed "Total Events"
    if (String(label).trim().toLowerCase() === "total") return;
    const count = c.counts ?? c.count ?? c.doc_count ?? 0;
    const accents: MetricCard["accent"][] = ["green", "blue", "red", "orange", "green", "yellow"];
    const safeLabel = String(label).trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-_]/g, "");
    metricCards.push({
      id: `evt-${safeLabel || "event"}-${i}`,
      label,
      value: count,
      icon: getMetricIcon(String(label), i),
      accent: accents[i % accents.length],
    });
  });

  const handleApply = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setAppliedLocationIds(draftLocationIds);
    setAppliedCameraIds(draftCameraIdsForPayload);
    setApplied(true);
    setGlobalApplyTrigger((prev) => prev + 1);
  };

  const handlePreset = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setFromDate(toDateString(from));
    setToDate(toDateString(to));
  };

  const SCROLL_AMOUNT = 600; // px per tick (like client-dashboard)
  const AUTO_SCROLL_INTERVAL = 3000; // ms
  const [isHoveringMetrics, setIsHoveringMetrics] = useState(false);

  const fixedCard = metricCards.find((m) => m.id === "total");
  const scrollableCards = metricCards.filter((m) => m.id !== "total");
  // Render each card once to avoid duplicate event cards in the metrics row.
  const duplicatedScrollableCards = scrollableCards;

  const scrollMetrics = (dir: "left" | "right") => {
    const el = metricsScrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT, behavior: "smooth" });
  };

  // Auto-slider: scroll metrics cards every 3s, reset when at end, pause on hover
  useEffect(() => {
    if (scrollableCards.length === 0 || isHoveringMetrics) return;
    const el = metricsScrollRef.current;
    if (!el) return;

    const interval = setInterval(() => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0 || isHoveringMetrics) return;

      el.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
      if (el.scrollLeft >= maxScroll - 100) {
        setTimeout(() => {
          if (!isHoveringMetrics) el.scrollTo({ left: 0, behavior: "smooth" });
        }, 1000);
      }
    }, AUTO_SCROLL_INTERVAL);

    return () => clearInterval(interval);
  }, [scrollableCards.length, isHoveringMetrics]);

  const accentIconBg: Record<string, string> = {
    magenta: "bg-blue-500/20 text-blue-700 dark:text-blue-400",
    blue: "bg-brand-violet/20 text-brand-purple",
    green: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
    red: "bg-rose-500/20 text-rose-700 dark:text-rose-400",
    orange: "bg-amber-500/20 text-amber-800 dark:text-amber-400",
    yellow: "bg-yellow-500/20 text-yellow-800 dark:text-yellow-400",
  };

  const sectionCard =
    "rounded-2xl border border-slate-200 bg-white shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-[#1C202F]/45 dark:shadow-none";
  const fieldSurface =
    "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100 dark:hover:bg-[#1C202F]/55";
  const inputDatetime =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 [color-scheme:light] dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100 dark:[color-scheme:dark]";
  const dropdownPanel =
    "absolute z-[140] max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700/80 dark:bg-[#1C202F]/95 dark:shadow-none";
  const dropdownRowHeader =
    "w-full border-b border-slate-200 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700";
  const dropdownRowLabel =
    "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-slate-800 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700";
  const dropdownPopover =
    "absolute z-50 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg metrics-scroll-hide dark:border-slate-700/80 dark:bg-[#1C202F]/95 dark:shadow-none";
  const filterSelectBtn = `flex min-w-[140px] items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 ${fieldSurface}`;
  const chartCardShell =
    "min-h-[350px] rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700/70 dark:bg-[#1C202F]/55";
  const primaryActionBtn =
    "border-slate-300 bg-slate-100 text-blue-700 transition-colors hover:border-blue-500/40 hover:bg-slate-200 dark:border-[#334155] dark:bg-[#1C202F] dark:text-blue-300 dark:hover:bg-[#1C202F]/70";

  return (
    <div className="min-h-full text-slate-900 dark:text-slate-100">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">
          Visual Charts & Analytics Insights
        </h1>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg  px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${primaryActionBtn}`}
        >
          <RefreshCw className="h-4 w-4" />
       
        </button>
      </div>

      {/* Smart Filters */}
      <section className={`${sectionCard} relative z-[130] mb-6 p-6`}>
        <div className="mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Smart Filters</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Configure your analytics view with precision
            </p>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-5 items-end justify-between gap-4">
          <div className="w-full">
            <label className="mb-1 block text-xs text-slate-600 dark:text-slate-400">From Date</label>
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              className={inputDatetime}
            />
          </div>
          <div className="w-full">
            <label className="mb-1 block text-xs text-slate-600 dark:text-slate-400">To Date</label>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className={inputDatetime}
            />
          </div>
          <div className=" w-full">
            <label className="mb-1 block text-xs text-slate-600 dark:text-slate-400">Locations</label>
            <div className="relative min-w-[180px]" ref={locationsRef}>
              {selectionToast?.source === "location" && (
                <div className="absolute bottom-full left-0 mb-1 z-[120] rounded-lg border border-amber-400/40 bg-amber-500/15 px-3 py-2 text-xs font-medium text-amber-300 shadow-lg backdrop-blur-sm whitespace-nowrap">
                  {selectionToast.message}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  setLocationsOpen((o) => !o);
                  setEventsDropdownOpen(false);
                  setClassesDropdownOpen(false);
                  setStatusDropdownOpen(false);
                  setSlotsDropdownOpen(false);
                  setAnalyticsSlotsDropdownOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 ${fieldSurface}`}
              >
                <span>
                  {locationsLoading
                    ? "Loading..."
                    : `${selectedLocations.length} selected`}
                </span>
                <div className="flex justify-end gap-2 items-center">
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-500 text-white text-xs px-2 py-0.5 rounded-full flex justify-end items-center">
                    {locationsLoading ? "-" : locationSelectionDisplayCount}
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                </div>
              </button>
              {locationsOpen && (
                <div
                  className={`${dropdownPanel} left-0 top-full mt-1 w-full max-h-60`}
                >
                  {allLocationsLoaded && (
                    <button
                      type="button"
                      className={dropdownRowHeader}
                      onClick={() => {
                        setAutoSelectAllLocations(!isAllSelected);
                        setSelectedLocations(isAllSelected ? [] : allLocationIds);
                        setLocationsOpen(false);
                      }}
                    >
                      {isAllSelected ? "Unselect all" : "Select all"}
                    </button>
                  )}
                  <div className="sticky top-0 z-[1] border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-600 dark:bg-[#1C202F]/95">
                    <input
                      type="text"
                      value={locationSearchText}
                      onChange={(e) => setLocationSearchText(e.target.value)}
                      placeholder="Search locations..."
                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100"
                    />
                  </div>
                  {filteredLocations.map((loc: any) => {
                    const id = loc.id || loc.value;
                    const checked = selectedLocations.includes(id);
                    return (
                      <label key={id} className={dropdownRowLabel}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAutoSelectAllLocations(false);
                              setSelectedLocations((p) => [...p, id]);
                            } else {
                              setSelectedLocations((p) => {
                                // Prevent removing the last selected location.
                                if (p.length <= 1) {
                                  showSelectionToast("At least one location must be selected.", "location");
                                  return p;
                                }
                                setAutoSelectAllLocations(false);
                                return p.filter((x) => x !== id);
                              });
                            }
                          }}
                          className="rounded border-slate-400 dark:border-slate-500"
                        />
                        {loc.label || loc.name || id}
                      </label>
                    );
                  })}
                  {filteredLocations.length === 0 && (
                    <div className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">No locations found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full">
            <label className="mb-1 flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <Camera className="h-3.5 w-3.5" /> Cameras
            </label>
            <div className="relative min-w-[180px]" ref={camerasRef}>
              {selectionToast?.source === "camera" && (
                <div className="absolute bottom-full left-0 mb-1 z-[120] rounded-lg border border-amber-400/40 bg-amber-500/15 px-3 py-2 text-xs font-medium text-amber-300 shadow-lg backdrop-blur-sm whitespace-nowrap">
                  {selectionToast.message}
                </div>
              )}
              <button
                type="button"
                disabled={!hasLocationsSelected}
                onClick={() => {
                  if (!hasLocationsSelected) return;
                  setCamerasOpen((o) => !o);
                  setLocationsOpen(false);
                  setEventsDropdownOpen(false);
                  setClassesDropdownOpen(false);
                  setStatusDropdownOpen(false);
                  setSlotsDropdownOpen(false);
                  setAnalyticsSlotsDropdownOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                  hasLocationsSelected
                    ? `${fieldSurface} cursor-pointer`
                    : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500 opacity-60 dark:border-[#334155] dark:bg-[#0B0F19]/30"
                }`}
              >
                <span>
                  {!hasLocationsSelected
                    ? "Select locations first"
                    : camerasLoading
                    ? "Loading..."
                    : `${selectedCameraCount} selected`}
                </span>
                <div className="flex justify-end gap-2 items-center">
                  {hasLocationsSelected && (
                    <span className="bg-gradient-to-r from-blue-600 to-indigo-500 text-white text-xs px-2 py-0.5 rounded-full flex justify-end items-center">
                      {camerasLoading ? "-" : cameraSelectionDisplayCount}
                    </span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 ${hasLocationsSelected ? "text-slate-500 dark:text-slate-400" : "text-slate-400 dark:text-slate-500"}`}
                  />
                </div>
              </button>
              {hasLocationsSelected && camerasOpen && (
                <div
                  className={`${dropdownPanel} left-0 top-full mt-1 w-full max-h-60`}
                >
                  {allCamerasLoaded && (
                    <button
                      type="button"
                      className={dropdownRowHeader}
                      onClick={() => {
                        setAutoSelectAllCameras(!isAllCamerasSelected);
                        setSelectedCameras(isAllCamerasSelected ? [] : allCameraIds);
                        setCamerasOpen(false);
                      }}
                    >
                      {isAllCamerasSelected ? "Unselect all" : "Select all"}
                    </button>
                  )}
                  <div className="sticky top-0 z-[1] border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-600 dark:bg-[#1C202F]/95">
                    <input
                      type="text"
                      value={cameraSearchText}
                      onChange={(e) => setCameraSearchText(e.target.value)}
                      placeholder="Search cameras..."
                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100"
                    />
                  </div>
                  {filteredCameras.map((cam: any) => {
                    const id = cam.id ?? cam.value;
                    const checked = selectedCameras.includes(id);
                    return (
                      <label key={id} className={dropdownRowLabel}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAutoSelectAllCameras(false);
                              setSelectedCameras((p) => [...p, id]);
                            } else {
                              setSelectedCameras((p) => {
                                const inScope = p.filter((camId) => allCameraIds.includes(camId));
                                // Prevent removing the last selected camera in the current location set.
                                if (inScope.length <= 1) {
                                  showSelectionToast("At least one camera must be selected.", "camera");
                                  return p;
                                }
                                setAutoSelectAllCameras(false);
                                return p.filter((x) => x !== id);
                              });
                            }
                          }}
                          className="rounded border-slate-400 dark:border-slate-500"
                        />
                        {cam.label ?? cam.name ?? id}
                      </label>
                    );
                  })}
                  {filteredCameras.length === 0 && (
                    <div className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">No cameras found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleApply}
            disabled={!canApplyFilters}
            className={`flex w-full items-center justify-center gap-2 rounded-lg border px-5 py-2 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
              canApplyFilters
                ? primaryActionBtn
                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-60 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-500"
            }`}
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>
        </div>

        <div className="mb-3 ">
          <label className="mb-2 block text-xs text-slate-600 dark:text-slate-400">Quick Time Presets</label>
          <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {QUICK_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => handlePreset(p.days)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-300 dark:hover:bg-[#1C202F]/55 dark:hover:text-white"
              >
                {p.label}
              </button>
            ))}
          </div>


          {applied && (
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1.5 text-sm text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-400">
              <MapPin className="w-4 h-4" />
              {isAllSelected ? locationCount : selectedLocations.length} locations selected
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Key Metrics Overview */}
      <section className={`${sectionCard} p-6`}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
              <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Key Metrics Overview
            </h2>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
              Statistics and performance indicators
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scrollMetrics("left")}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition-colors hover:border-blue-500/40 hover:bg-slate-100 hover:text-slate-900 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-300 dark:hover:bg-[#1C202F]/55 dark:hover:text-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => scrollMetrics("right")}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition-colors hover:border-blue-500/40 hover:bg-slate-100 hover:text-slate-900 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-300 dark:hover:bg-[#1C202F]/55 dark:hover:text-white"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex gap-4 items-stretch">
          <>
              {/* Fixed Total Events card */}
              {fixedCard && (
                <div
                  className="w-[200px] flex-shrink-0 rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-slate-300 dark:border-slate-700/70 dark:bg-[#1C202F]/55 dark:hover:border-slate-600/90"
                  onMouseEnter={() => setIsHoveringMetrics(true)}
                  onMouseLeave={() => setIsHoveringMetrics(false)}
                >
                  <div className="mb-2 flex items-start justify-between">
                    <div className={`rounded-lg p-2 ${accentIconBg[fixedCard.accent]}`}>
                      {fixedCard.icon}
                    </div>
                    {fixedCard.badge && (
                      <span className="rounded-full bg-gradient-to-r from-blue-600/25 to-indigo-500/25 px-2 py-0.5 text-xs text-blue-700 dark:from-blue-600/30 dark:to-indigo-500/30 dark:text-blue-400">
                        {fixedCard.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{fixedCard.label}</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">
                    {typeof fixedCard.value === "number"
                      ? fixedCard.value.toLocaleString()
                      : fixedCard.value}
                  </p>
                </div>
              )}
              {/* Scrollable cards (duplicated for infinite scroll illusion) */}
              <div
                ref={metricsScrollRef}
                className="metrics-scroll-hide flex min-w-0 flex-1 gap-4 overflow-x-auto overflow-y-hidden scroll-smooth pb-2"
                onMouseEnter={() => setIsHoveringMetrics(true)}
                onMouseLeave={() => setIsHoveringMetrics(false)}
              >
                {duplicatedScrollableCards.map((m, idx) => (
                  <div
                    key={`${m.id}-${idx}`}
                    className="w-[200px] flex-shrink-0 rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-slate-300 dark:border-slate-700/70 dark:bg-[#1C202F]/55 dark:hover:border-slate-600/90"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className={`rounded-lg p-2 ${accentIconBg[m.accent]}`}>
                        {m.icon}
                      </div>
                      {m.badge && (
                        <span className="rounded-full bg-gradient-to-r from-blue-600/25 to-indigo-500/25 px-2 py-0.5 text-xs text-blue-700 dark:from-blue-600/30 dark:to-indigo-500/30 dark:text-blue-400">
                          {m.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{m.label}</p>
                    <p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">
                      {typeof m.value === "number"
                        ? m.value.toLocaleString()
                        : m.value}
                    </p>
                  </div>
                ))}
              </div>
          </>
        </div>
      </section>

      {/* Events Analytics - Filter + Charts */}
      {eventsElasticRows.length > 0 && (
      <section className={`${sectionCard} mt-6 p-6`}>
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-violet">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Events Analytics</h2>
            
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative" ref={eventsDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setEventsDropdownOpen((o) => !o);
                  setClassesDropdownOpen(false);
                  setStatusDropdownOpen(false);
                  setSlotsDropdownOpen(false);
                }}
                className={filterSelectBtn}
              >
                <span>
                  {selectedEvents.length === 0
                    ? "All Events"
                    : `${selectedEvents.length} event${selectedEvents.length > 1 ? "s" : ""}`}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              </button>
              {eventsDropdownOpen && (
                <div className={`${dropdownPopover} left-0 top-full mt-1 max-h-48 w-56`}>
                  {cleanedEventsData.map((evt: any) => (
                    <label key={evt.value} className={dropdownRowLabel}>
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(evt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEvents((p) => [...p, evt.value]);
                          } else {
                            setSelectedEvents((p) => p.filter((x) => x !== evt.value));
                          }
                        }}
                        className="rounded border-slate-400 dark:border-slate-500"
                      />
                      {evt.label || evt.value}
                    </label>
                  ))}
                  {cleanedEventsData.length === 0 && (
                    <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">No events</div>
                  )}
                </div>
              )}
            </div>
            <div className="relative" ref={statusDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setStatusDropdownOpen((o) => !o);
                  setSlotsDropdownOpen(false);
                  setEventsDropdownOpen(false);
                }}
                className={filterSelectBtn}
              >
                <span>
                  {selectedStatus.length === 0
                    ? "All Status"
                    : `${selectedStatus.length} status`}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              </button>
              {statusDropdownOpen && (
                <div className={`${dropdownPopover} left-0 top-full mt-1 max-h-48 w-48`}>
                  {statusOptions.map((opt) => (
                    <label key={opt} className={dropdownRowLabel}>
                      <input
                        type="checkbox"
                        checked={selectedStatus.includes(opt)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStatus((p) => [...p, opt]);
                          } else {
                            setSelectedStatus((p) => p.filter((x) => x !== opt));
                          }
                        }}
                        className="rounded border-slate-400 dark:border-slate-500"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={slotsDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setSlotsDropdownOpen((o) => !o);
                  setStatusDropdownOpen(false);
                  setEventsDropdownOpen(false);
                }}
                className={filterSelectBtn}
              >
                <span>
                  {selectedSlots.length === 0
                    ? "All Slots"
                    : `${selectedSlots.length} slot${selectedSlots.length > 1 ? "s" : ""}`}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              </button>
              {slotsDropdownOpen && (
                <div className={`${dropdownPopover} left-0 top-full mt-1 max-h-48 w-48`}>
                  {eventSlotsOptions.map((opt) => (
                    <label key={opt} className={dropdownRowLabel}>
                      <input
                        type="checkbox"
                        checked={selectedSlots.includes(opt)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSlots((p) => [...p, opt]);
                          } else {
                            setSelectedSlots((p) => p.filter((x) => x !== opt));
                          }
                        }}
                        className="rounded border-slate-400 dark:border-slate-500"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setAppliedEventsFilters({
                  ...(selectedEvents.length > 0 ? { Events: selectedEvents } : {}),
                  ...(selectedStatus.length > 0 ? { Status: selectedStatus } : {}),
                  ...(selectedSlots.length > 0 ? { Slots: selectedSlots } : {}),
                });
                setEventsElasticApplyTrigger((prev) => prev + 1);
              }}
              className={`flex items-center gap-2 rounded-lg border px-5 py-2 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500/40 ${primaryActionBtn}`}
            >
              <Filter className="w-4 h-4" />
              Apply
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {eventsElasticRows.map((row) => (
              <div
                key={row.id}
                className={`${chartCardShell} ${
                  row.type === "stack" ? "md:col-span-2" : ""
                } ${row.type === "pie" || row.type === "slot" ? "md:col-span-1" : ""}`}
              >
                <OverviewElasticChart
                  row={{ id: row.id, title: row.title, type: row.type }}
                  clientId={effectiveClientId}
                  fromIso={baseParams.from}
                  toIso={baseParams.to}
                  locationIds={appliedLocationIdsForData}
                  cameraIds={appliedCameraIdsForData}
                  applyGeneration={eventsElasticGeneration}
                  filtersSynced={elasticChartsFiltersReady}
                  filterSection="events-analytics"
                  sectionFilters={appliedEventsFilters}
                />
              </div>
            ))}
        </div>
      </section>
      )}

      {/* Analytic Graphs - Filter + Charts */}
      {analyticsElasticRows.length > 0 && (
      <section className={`${sectionCard} mt-6 p-6`}>
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-violet">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Analytic Graphs</h2>
              
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative" ref={analyticsSlotsDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setAnalyticsSlotsDropdownOpen((o) => !o);
                  setClassesDropdownOpen(false);
                }}
                className={filterSelectBtn}
              >
                <span>
                  {selectedAnalyticsSlots.length === 0
                    ? "All Slots"
                    : `${selectedAnalyticsSlots.length} slot${selectedAnalyticsSlots.length > 1 ? "s" : ""}`}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              </button>
              {analyticsSlotsDropdownOpen && (
                <div className={`${dropdownPopover} left-0 top-full mt-1 max-h-48 w-48`}>
                  {analyticsSlotsOptions.map((opt) => (
                    <label key={opt} className={dropdownRowLabel}>
                      <input
                        type="checkbox"
                        checked={selectedAnalyticsSlots.includes(opt)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAnalyticsSlots((p) => [...p, opt]);
                          } else {
                            setSelectedAnalyticsSlots((p) => p.filter((x) => x !== opt));
                          }
                        }}
                        className="rounded border-slate-400 dark:border-slate-500"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={classesDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setClassesDropdownOpen((o) => !o);
                  setEventsDropdownOpen(false);
                  setAnalyticsSlotsDropdownOpen(false);
                }}
                className={filterSelectBtn}
              >
                <span>
                  {selectedClasses.length === 0
                    ? "All Classes"
                    : `${selectedClasses.length} class${selectedClasses.length > 1 ? "es" : ""}`}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              </button>
              {classesDropdownOpen && (
                <div className={`${dropdownPopover} left-0 top-full mt-1 max-h-48 w-56`}>
                  {cleanedClassesData.map((cls: any) => (
                    <label key={cls.value} className={dropdownRowLabel}>
                      <input
                        type="checkbox"
                        checked={selectedClasses.includes(cls.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClasses((p) => [...p, cls.value]);
                          } else {
                            setSelectedClasses((p) => p.filter((x) => x !== cls.value));
                          }
                        }}
                        className="rounded border-slate-400 dark:border-slate-500"
                      />
                      {cls.label || cls.value}
                    </label>
                  ))}
                  {cleanedClassesData.length === 0 && (
                    <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">No classes</div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setAppliedAnalyticsFilters({
                  ...(selectedAnalyticsSlots.length > 0 ? { Slots: selectedAnalyticsSlots } : {}),
                  ...(selectedClasses.length > 0 ? { Classes: selectedClasses } : {}),
                });
                setAnalyticsElasticApplyTrigger((prev) => prev + 1);
              }}
              className={`flex items-center gap-2 rounded-lg border px-5 py-2 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500/40 ${primaryActionBtn}`}
            >
              <Filter className="w-4 h-4" />
              Apply
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {analyticsElasticRows.map((row) => (
              <div
                key={row.id}
                className={`${chartCardShell} ${
                  row.type === "stack" || row.type === "line" || row.type === "bar" ? "md:col-span-2" : ""
                } ${row.type === "pie" || row.type === "slot" ? "md:col-span-1" : ""}`}
              >
                <OverviewElasticChart
                  row={{ id: row.id, title: row.title, type: row.type }}
                  clientId={effectiveClientId}
                  fromIso={baseParams.from}
                  toIso={baseParams.to}
                  locationIds={appliedLocationIdsForData}
                  cameraIds={appliedCameraIdsForData}
                  applyGeneration={analyticsElasticGeneration}
                  filtersSynced={elasticChartsFiltersReady}
                  filterSection="analytic-graphs"
                  sectionFilters={appliedAnalyticsFilters}
                />
              </div>
            ))}
        </div>
      </section>
      )}
    </div>
  );
}
